import _ from "lodash"
import moment from "moment"
import countries from "i18n-iso-countries"

import { APIConfig } from "@oj-x/config"
import { getFromLocalStorage } from "services/local-storage"

// Lazy import locales in their own chunk
// eslint-disable-next-line no-unused-expressions
import(
  /* webpackChunkName: "moment-locales" */
  /* webpackMode: "lazy" */
  "moment/min/locales"
)

/**
 * Sets moment.js locale
 * @param {string} locale
 * @returns {void}
 */
const setMomentLocale = locale => {
  moment.locale(locale)
}

/**
 * Gets the initial app locale based on the the following order:
 * deeplink > localStorage > browser
 * If none of these are set or valid, we take the first option from supportedLocales API config
 * @param {string} deeplinkLocale, The requested user locale.
 * @returns {string} A matching locale string value
 */
const getInitialLocale = deeplinkLocale => {
  const storedLocale = _.get(getFromLocalStorage("locale"), "value")
  const browserLocale = navigator.language ? navigator.language : navigator.browserLanguage

  // we use the locale based on this order: deeplink > stored > browser
  const initialLocale = deeplinkLocale || storedLocale || browserLocale

  // make sure the initial locale is supported, otherwise uses the first one in the list
  const supportedLocales = APIConfig.getValueAsArray("supportedLocales", [])

  if (_.includes(supportedLocales, initialLocale)) {
    return initialLocale
  }

  // set en-AU AS default
  return "en-AU"

  // If this is not an accepted locale, return the default (first) locale
  // return _.first(supportedLocales)
}

const getLanguageFromLocale = locale => {
  return _.first(_.split(locale, "-"))
}

const getLanguagesFromLocales = () => {
  const supportedLocales = APIConfig.getValueAsArray("supportedLocales", [])
  const uniqueLanguageCodes = _.uniq(
    _.map(supportedLocales, locale => getLanguageFromLocale(locale))
  )
  return _.map(uniqueLanguageCodes, code => ({ code }))
}

const getLocalizedCurrentDate = () => {
  return moment.localeData().longDateFormat("L")
}

export default {
  getInitialLocale,
  getLanguageFromLocale,
  getLanguagesFromLocales,
  getLocalizedCurrentDate,
  setMomentLocale
}

/**
 * Gets singular or plural language key depending on parameter count
 * In language file we will have two labes:
 *  - key: used for singular text
 *  - keyPlural: used for plural text
 * ie:
 *   "carResultsHeaderCountLabel": " car"
 *   "carResultsHeaderCountLabelPlural": " cars"
 * @param {number} count, if it is more than 1, we will return the plural key.
 * @param {string} key, key to pluralize
 * @returns {string} key.
 */
export const getPluralization = (count, key) => {
  return Math.abs(count) > 1 ? `${key}Plural` : key
}

export const translationExists = translate => key => {
  return translate(key) && translate(key) !== key
}

// Verifies if the translation key exists and if it is empty
export const isTranslationEmpty = translate => key => {
  return translationExists(key) && _.isEmpty(translate(key))
}

export const getCountryNames = language => {
  const countryList = countries.getNames(language)

  if (!_.isEmpty(countryList)) {
    return countryList
  }

  countries.registerLocale(require(`i18n-iso-countries/langs/${language}.json`))
  return countries.getNames(language)
}
